import { toPng } from 'html-to-image';
import { getAuthHeaders } from './api';

function apiBase() {
  let b = process.env.REACT_APP_API_BASE_URL || '';
  if (!b && /localhost|127\.0\.0\.1/.test(window.location.hostname)) b = 'http://localhost:5001';
  return b.replace(/\/$/, '');
}

/**
 * Download the event newsletter as a PNG, rendered ENTIRELY in the browser.
 * The backend returns a self-contained HTML (all images inlined as data-URIs); we render it
 * off-screen and rasterize with html-to-image. No server-side Chromium (Azure can't run it).
 */
/** Fetch the self-contained landscape banner HTML (all images inlined) for preview/rasterization. */
export async function fetchBannerHtml(eventKey, theme = '') {
  const q = theme ? `?theme=${encodeURIComponent(theme)}` : '';
  const res = await fetch(
    `${apiBase()}/api/manage/calendar-events/${encodeURIComponent(eventKey)}/banner-html${q}`,
    { credentials: 'include', headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Banner failed (${res.status})`);
  return res.text();
}

/**
 * Download the landscape "website scrolling image" banner (1800×720) as a PNG, rendered in-browser.
 * The banner HTML is fully self-contained; we load it into an offscreen iframe and rasterize its body
 * with html-to-image (no server Chromium — same reason as the poster).
 */
export async function downloadEventBanner(eventKey, theme = '') {
  const html = await fetchBannerHtml(eventKey, theme);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:0;top:0;width:1800px;height:720px;border:0;z-index:-1;pointer-events:none;opacity:0.01;';
  document.body.appendChild(iframe);
  try {
    await new Promise((resolve) => { iframe.onload = resolve; iframe.srcdoc = html; });
    const doc = iframe.contentDocument;
    const imgs = Array.from(doc.querySelectorAll('img'));
    await Promise.all(imgs.map((im) => (im.complete ? Promise.resolve() : new Promise((r) => { im.onload = im.onerror = r; }))));
    await new Promise((r) => setTimeout(r, 200));
    const body = doc.body;
    let dataUrl = '';
    for (let i = 0; i < 3; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      dataUrl = await toPng(body, { pixelRatio: 2, width: 1800, height: 720, cacheBust: true });
    }
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${eventKey}-website-banner.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    document.body.removeChild(iframe);
  }
}

export async function downloadEventPoster(eventKey) {
  const res = await fetch(
    `${apiBase()}/api/manage/calendar-events/${encodeURIComponent(eventKey)}/poster-html`,
    { credentials: 'include', headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Poster failed (${res.status})`);
  const html = await res.text();

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const card = doc.querySelector('table[width="680"]');
  if (!card) throw new Error('Could not build the poster.');
  // Flatten to a clean full-bleed rectangle.
  card.style.borderRadius = '0';
  card.style.boxShadow = 'none';
  card.style.maxWidth = 'none';
  card.style.margin = '0';

  // Must render IN the viewport for html-to-image, but behind the page (z-index:-1) so it's not seen.
  const holder = document.createElement('div');
  holder.style.cssText = 'position:fixed;left:0;top:0;width:680px;background:#ffffff;z-index:-1;pointer-events:none;';
  holder.appendChild(document.importNode(card, true));
  document.body.appendChild(holder);
  try {
    // Wait for every inlined image to finish decoding, then let layout settle.
    const imgs = Array.from(holder.querySelectorAll('img'));
    await Promise.all(imgs.map((im) => (im.complete ? Promise.resolve() : new Promise((r) => { im.onload = im.onerror = r; }))));
    await new Promise((r) => setTimeout(r, 150));
    // html-to-image is blank on the first pass(es) until images embed — warm up, keep the last.
    let dataUrl = '';
    for (let i = 0; i < 3; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      dataUrl = await toPng(holder, {
        pixelRatio: 2, backgroundColor: '#ffffff', width: 680, height: holder.scrollHeight, cacheBust: true,
      });
    }
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${eventKey}-poster.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    document.body.removeChild(holder);
  }
}
